// Modal de "Regerar" do sudoku (catálogo de Jogos).
//
// Único dos três regenerates que não exige input: o body é opcional. A dificuldade é a
// única escolha, e deixá-la vazia mantém a atual.
//
// A spec previa um Popconfirm, mas um Select dentro de Popconfirm é frágil (o dropdown
// abre em portal e o clique fora fecha o popover). Modal evita o problema e ainda dá
// espaço para o aviso de que a grade será substituída.
import { useState } from 'react';
import { Alert, Form, Modal, Select, message } from 'antd';

import { regenerateSudoku, type JogoAdmin, type SudokuDificuldade } from '../../services/admin';

interface FormValues {
  dificuldade?: SudokuDificuldade;
}

interface Props {
  /** Jogo a regerar; `null` mantém o modal fechado. */
  target: JogoAdmin | null;
  onClose: () => void;
  onDone: () => void;
}

// O backend responde com `{ msg }` (erros do domínio) ou `{ statusCode, message, error }`
// (HttpExceptionFilter global); `message` pode vir como lista.
function errorMessage(err: any): string {
  const data = err?.response?.data;
  const raw = data?.msg ?? data?.message;
  const text = Array.isArray(raw) ? raw.join(' ') : raw;
  return text || 'Falha ao regerar o sudoku.';
}

export const RegenerateSudokuModal: React.FC<Props> = ({ target, onClose, onDone }) => {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!target) return;
    const values = await form.validateFields();

    setSaving(true);
    try {
      // Sem dificuldade escolhida, manda `{}` — o backend mantém a dificuldade atual.
      await regenerateSudoku(
        target.idJogo,
        values.dificuldade ? { dificuldade: values.dificuldade } : {},
      );
      message.success('Sudoku regerado.');
      onDone();
      onClose();
    } catch (err: any) {
      message.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={target ? `Regerar sudoku #${target.idJogo}` : 'Regerar sudoku'}
      visible={target !== null}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Regerar"
      okButtonProps={{ danger: true }}
      cancelText="Cancelar"
      confirmLoading={saving}
      destroyOnClose
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="A grade atual do sudoku será substituída por uma nova."
      />
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="dificuldade"
          label="Dificuldade"
          extra="Deixe vazio para manter a dificuldade atual."
        >
          <Select allowClear placeholder="Manter a atual" style={{ width: 200 }}>
            <Select.Option value="facil">Fácil</Select.Option>
            <Select.Option value="medio">Médio</Select.Option>
            <Select.Option value="dificil">Difícil</Select.Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RegenerateSudokuModal;
